import articlesIndex from '../data/articles/index.json';
import { scoreArticleRelevance } from '../utils/search';
import { config } from '../config/env';

// Helper function to clean article titles
function cleanArticleTitle(title) {
  return title
    .replace(/^RHBlog[_ ]/i, '')     // Remove RHBlog prefix with underscore or space
    .replace(/^RH Blog[_ ]/i, '')     // Remove RH Blog prefix with underscore or space
    .replace(/^RH[_ ]/i, '')          // Remove RH prefix with underscore or space
    .replace(/_/g, ' ')               // Replace underscores with spaces
    .replace(/\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\s+\d{2}\b/i, '') // Remove month year
    .replace(/\s+/g, ' ')             // Replace multiple spaces with single space
    .trim();                          // Remove extra spaces
}

// Update the findRelevantArticles function
async function findRelevantArticles(query) {
  const searchTerms = query.toLowerCase().split(' ');
  
  // Score and sort articles
  const scoredArticles = articlesIndex.articles
    .map(article => ({
      ...article,
      title: cleanArticleTitle(article.title), // Clean the title
      score: scoreArticleRelevance(article, searchTerms)
    }))
    .filter(article => article.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3);

  // Load full content for top-scoring articles
  const articles = await Promise.all(
    scoredArticles.map(async ({ id }) => {
      try {
        const article = await import(`../data/articles/${id}.json`);
        return {
          ...article.default,
          title: cleanArticleTitle(article.default.title) // Clean the title here too
        };
      } catch (error) {
        console.error(`Error loading article ${id}:`, error);
        return null;
      }
    })
  );

  return articles.filter(article => article !== null);
}

// Add this helper function to convert markdown table to HTML
function convertMarkdownTableToHTML(markdownTable) {
  // Split the table into rows
  const rows = markdownTable.trim().split('\n');
  
  // Skip the separator row (the one with |---|---|)
  const headerRow = rows[0];
  const dataRows = rows.slice(2);
  
  // Convert header
  const headers = headerRow
    .split('|')
    .filter(cell => cell.trim())
    .map(cell => cell.trim());
    
  // Build HTML table
  let htmlTable = '<table class="ai-table">\n<thead>\n<tr>\n';
  
  // Add headers
  headers.forEach(header => {
    htmlTable += `  <th>${header}</th>\n`;
  });
  
  htmlTable += '</tr>\n</thead>\n<tbody>\n';
  
  // Add data rows
  dataRows.forEach(row => {
    const cells = row
      .split('|')
      .filter(cell => cell.trim())
      .map(cell => cell.trim());
      
    htmlTable += '<tr>\n';
    cells.forEach(cell => {
      htmlTable += `  <td>${cell}</td>\n`;
    });
    htmlTable += '</tr>\n';
  });
  
  htmlTable += '</tbody>\n</table>';
  
  return htmlTable;
}

export const chatWithAI = async (userInput, format) => {
  try {
    // First, find relevant articles
    const relevantArticles = await findRelevantArticles(userInput);
    const articlesContext = relevantArticles
      .map(article => `Article Title: ${article.title}\nContent: ${article.content}`)
      .join('\n\n');

    let systemPrompt = "You are a helpful AI assistant. ";
    
    // Add format-specific instructions with article context
    switch (format) {
      case 'table':
        systemPrompt += "Format your response as a markdown table. Include a header row and ensure each row has at least 2-3 columns with relevant information. Base your response on the provided articles' content. Use proper markdown table syntax with | for columns and proper header separation using |---| format.";
        break;
      case 'list':
        systemPrompt += "Format your response as a markdown bullet point list. Each point should be concise, start with a dash (-), and be based on information from the provided articles.";
        break;
      case 'paragraphs':
      default:
        systemPrompt += "Format your response in clear, well-structured paragraphs with appropriate spacing between ideas. Base your response on information from the provided articles.";
        break;
    }

    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`
      },
      body: JSON.stringify({
        model: 'gpt-3.5-turbo',
        messages: [
          {
            role: 'system',
            content: systemPrompt
          },
          {
            role: 'user',
            content: `Please use the following articles as reference for your response:\n\n${articlesContext}\n\nQuery: ${userInput}`
          }
        ],
        temperature: 0.7
      })
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const data = await response.json();
    const content = data.choices[0].message.content;

    return {
      type: format,
      content: format === 'table' ? convertMarkdownTableToHTML(content) : content
    };
  } catch (error) {
    console.error('Error in chatWithAI:', error);
    throw error;
  }
};

// Helper function to truncate content while keeping whole sentences
function truncateContent(content, maxLength) {
  if (content.length <= maxLength) return content;
  
  const truncated = content.substring(0, maxLength);
  const lastPeriod = truncated.lastIndexOf('.');
  
  return lastPeriod > 0 
    ? truncated.substring(0, lastPeriod + 1) 
    : truncated + '...';
} 